import { useEffect, useRef } from "react";
import useIsMobile from "./useIsMobile";

const TOP_INSET = 66;

/**
 * Scroll to the target position using every method available,
 * because iOS Safari is picky about which one actually works.
 */
function forceScroll(y) {
  window.scrollTo(0, y);
  if (document.scrollingElement) {
    document.scrollingElement.scrollTop = y;
  }
  document.documentElement.scrollTop = y;
  document.body.scrollTop = y;
}

/**
 * Scroll trap for liquid glass effect.
 * Scrolls down 66px and locks scroll there — but pauses
 * when an input/textarea is focused so the keyboard can
 * push the page up naturally.
 */
const useScrollTrap = (isActive) => {
  const isMobile = useIsMobile();
  const paused = useRef(false);

  useEffect(() => {
    if (!isActive || !isMobile) {
      forceScroll(0);
      return;
    }

    // Initial scroll attempts
    const raf = requestAnimationFrame(() => forceScroll(TOP_INSET));
    const t1 = setTimeout(() => forceScroll(TOP_INSET), 100);
    const t2 = setTimeout(() => forceScroll(TOP_INSET), 300);

    // Lock scroll — only when not paused for keyboard
    const lockScroll = (e) => {
      if (paused.current) return;
      e.preventDefault();
      forceScroll(TOP_INSET);
    };
    window.addEventListener("scroll", lockScroll, { passive: false });

    const blockTouch = (e) => {
      if (paused.current) return;
      e.preventDefault();
    };
    document.addEventListener("touchmove", blockTouch, { passive: false });

    // Pause trap when input is focused (keyboard opens)
    const onFocusIn = (e) => {
      const tag = e.target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target.isContentEditable) {
        paused.current = true;
      }
    };

    // Resume trap when input loses focus (keyboard closes)
    const onFocusOut = (e) => {
      const tag = e.target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target.isContentEditable) {
        // Small delay to let keyboard fully close before snapping back
        setTimeout(() => {
          paused.current = false;
          forceScroll(TOP_INSET);
        }, 300);
      }
    };

    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener("scroll", lockScroll);
      document.removeEventListener("touchmove", blockTouch);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
    };
  }, [isActive, isMobile]);
};

export default useScrollTrap;
